import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  effect,
  forwardRef,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { FormControl, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { BaseControlValueAccessor } from '../base-control-value-accessor';
import { LookupViewModel } from '../../../../data/models/ui-models';

@Component({
  selector: 'lib-form-dropdown',
  templateUrl: './form-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatSelectModule, MatFormFieldModule, NgClass],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormDropdownComponent),
      multi: true,
    },
  ],
})
export class FormDropdownComponent
  extends BaseControlValueAccessor<string>
  implements AfterContentInit
{
  private static nextId = 0;
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);

  readonly inputId = `form-dropdown-${FormDropdownComponent.nextId++}`;
  readonly errorId = `${this.inputId}-error`;
  label = input<string>('');
  hideRequiredMarker = input<boolean>(false);
  ariaLabel = input<string>('');
  placeholder = input<string>('');
  options = input<LookupViewModel[]>([]);
  defaultSelection = input<unknown>();
  multiple = input<boolean>(false);
  disabledSignal = input<boolean>(false);
  readonly = input<boolean>(false);
  required = input<boolean>(false);
  valueChange = output<string>();

  constructor() {
    super();

    effect(() => {
      const defaultSelection = this.defaultSelection();
      const options = this.options();
      if (defaultSelection && this.control) {
        const found = options.find((o) => o.id === defaultSelection || o.name === defaultSelection);
        if (found) this.control.setValue(found.id);
      }
    });

    effect(() => {
      const off = this.disabledSignal() || this.readonly();
      if (!this.control) return;
      if (off) this.control.disable({ emitEvent: false });
      else this.control.enable({ emitEvent: false });
    });
  }

  ngAfterContentInit(): void {
    const ngControl = this.injector.get(NgControl, null);
    this.control = (ngControl?.control as FormControl) ?? new FormControl();

    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.valueChange.emit(value));

    const defaultSelection = this.defaultSelection();
    if (defaultSelection) {
      const found = this.options().find(
        (o) => o.id === defaultSelection || o.name === defaultSelection,
      );
      if (found) this.control.setValue(found.id);
    }
    if (this.disabledSignal() || this.readonly()) {
      this.control.disable({ emitEvent: false });
    }
  }

  protected override getLabel(): string {
    return this.label() || '';
  }

  override get isRequired(): boolean {
    return this.required() || super.isRequired;
  }
}
